/**
 * Shared order-weight helpers for the seller app.
 *
 * `orderDetail` (written by Final Correction) stores PER-SET averages plus a
 * quantity, e.g.
 *   { "10": { qty: 3, grossWT: 6.2, netwt: 5.1, specWT: 1.1 } }
 * so every weight must be multiplied by `qty`. Reading `order.netWt` /
 * `order.grossWt` directly only ever returns ONE set's weight — which is why
 * the settlement cards used to show "Qty 5" next to a single set's gross/net.
 *
 * These helpers prefer the operator's `settlementData` snapshot (the figures
 * that were actually settled) and otherwise recompute from the per-size
 * breakdown.
 */

export const num = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const dQty = (d) => num(d?.qty ?? d?.set ?? d?.quantity) || 1;
const dNet = (d) => num(d?.netwt ?? d?.netWt);
const dGross = (d) => num(d?.grossWT ?? d?.grossWt);
const dSpec = (d) => {
  const explicit = d?.specWT ?? d?.specWt;
  return explicit !== undefined && explicit !== null && explicit !== ""
    ? num(explicit)
    : Math.max(0, dGross(d) - dNet(d));
};

const row = (size, qty, netPerSet, grossPerSet, specPerSet) => ({
  size,
  qty,
  netPerSet,
  grossPerSet,
  specPerSet,
  netWt: netPerSet * qty,
  grossWt: grossPerSet * qty,
  specWt: specPerSet * qty,
});

/** Per-size rows, each carrying the per-set figure AND the line total. */
export const getSizeBreakdown = (order = {}) => {
  const detail = order.orderDetail;
  const hasDetail =
    detail && typeof detail === "object" && Object.keys(detail).length > 0;

  if (hasDetail) {
    return Object.entries(detail).map(([size, d]) =>
      row(size, dQty(d), dNet(d), dGross(d), dSpec(d)),
    );
  }

  const variants = order.selectedVariants || [];
  const lots = order.lotDetails || [];

  if (variants.length) {
    return variants.map((v) => {
      const lot = lots.find((l) => l.size === v.size) || {};
      const qty = num(v.quantity) || 1;
      const netPerSet = num(lot.netWt ?? lot.avgNetWt ?? order.netWt);
      const grossPerSet = num(lot.grossWt ?? lot.avgGrossWt ?? order.grossWt);
      return row(
        v.size,
        qty,
        netPerSet,
        grossPerSet,
        Math.max(0, grossPerSet - netPerSet),
      );
    });
  }

  if (lots.length) {
    return lots.map((l) => {
      const qty = num(l.set ?? l.qty) || 1;
      const netPerSet = num(l.netWt ?? l.avgNetWt ?? order.netWt);
      const grossPerSet = num(l.grossWt ?? l.avgGrossWt ?? order.grossWt);
      return row(
        l.size,
        qty,
        netPerSet,
        grossPerSet,
        Math.max(0, grossPerSet - netPerSet),
      );
    });
  }

  // No breakdown at all — fall back to the order-level fields.
  const qty = num(order.totalQty || order.quantity || order.orderPiece) || 1;
  const netPerSet = num(order.netWt);
  const grossPerSet = num(order.grossWt);
  return [
    row(
      "—",
      qty,
      netPerSet,
      grossPerSet,
      num(order.specificationWt) || Math.max(0, grossPerSet - netPerSet),
    ),
  ];
};

/**
 * Authoritative qty / gross / net / spec for a settled order.
 * Prefers the operator's `settlementData` snapshot so the seller card always
 * matches the number that was actually settled.
 */
export const getOrderWeights = (order = {}) => {
  const sd = order.settlementData || {};
  const rows = getSizeBreakdown(order);

  const computed = {
    qty: rows.reduce((s, r) => s + r.qty, 0) || 1,
    netWt: rows.reduce((s, r) => s + r.netWt, 0),
    grossWt: rows.reduce((s, r) => s + r.grossWt, 0),
    specWt: rows.reduce((s, r) => s + r.specWt, 0),
  };

  return {
    qty: num(sd.TotalSet) || computed.qty,
    netWt: num(sd.TotalnetWt) || computed.netWt,
    grossWt: num(sd.TotalgrossWt) || computed.grossWt,
    specWt: num(sd.specificationWt) || computed.specWt,
    rows,
  };
};

/** Seller's making charges for one order (prefers the settled snapshot). */
export const getOrderSettledAmount = (order = {}) => {
  const sd = order.settlementData || {};
  const direct =
    num(sd.totalAmountSeller) ||
    num(sd.totalAmount) ||
    num(order.totalAmount) ||
    num(order.totalMc) ||
    num(order.totalMC) ||
    num(order.makingCharges) ||
    num(order.makingCharge) ||
    num(order.mc) ||
    num(order.amount);
  if (direct > 0) return direct;

  // Last resort: recompute MC = setMc x qty + gramMc x netWt + specMc x specWt
  const w = getOrderWeights(order);
  const setMc = num(order.setMc || order.setMakingCharge);
  const gramMc = num(order.netGramMc || order.gramMc || order.makingChargePerGram);
  const specMc = num(
    order.specificationGramRate || order.specMcPerGram || order.specGramRate,
  );
  return setMc * w.qty + gramMc * w.netWt + specMc * w.specWt;
};

/** Fine grams for one order (prefers the settled snapshot). */
export const getOrderSettledFineWt = (order = {}) => {
  const sd = order.settlementData || {};
  const direct =
    num(sd.fineWeight) ||
    num(sd.sellerFine) ||
    num(order.fineWeight) ||
    num(order.totalFine) ||
    num(order.fineWt) ||
    num(order.totalFineWeight);
  if (direct > 0) return direct;

  const w = getOrderWeights(order);
  if (w.netWt === 0) return 0;
  const addOn =
    (num(order.purity) + num(order.wastage) + num(order.specificationMC)) / 100;
  return addOn > 0 ? Number((w.netWt * addOn).toFixed(3)) : 0;
};
